import React, { useMemo, useState, useRef, useEffect } from 'react'
import { COMPASS_16 } from '../utils/windDirections'
import './WindRosePrototypes.css'

/**
 * OPTION 4 — Rose + legend + interactive wind-direction ring (combines ① + ②).
 *
 * Interaction model (v2, after "clunky" feedback):
 * - Default: full rose (every site as a coloured arc) + synced legend of all
 *   sites — the labelled overview.
 * - The OUTER compass ring filters by wind direction. To avoid the earlier
 *   flicker/"stuck" feel, selection is CLICK-TO-LOCK, with a light HOVER
 *   PREVIEW:
 *     • Hovering a ring sector previews that direction (soft highlight/fade).
 *     • Clicking a sector LOCKS it (persists when the mouse leaves the ring, so
 *       you can then freely mouse over the site arcs). Click the same sector,
 *       or the Clear button, to release.
 *   Effective direction = hovered (preview) ?? locked.
 * - Hovering a site arc/legend row highlights that individual site and does NOT
 *   change the direction filter (the two interactions are decoupled).
 * - Faded (non-matching) arcs keep a readable opacity floor and stay hoverable.
 */

const SIZE = 340
const CX = SIZE / 2
const CY = SIZE / 2
const RING_MIN = 66
const RING_MAX = 146
const RING_SECTOR_IN = 150
const RING_SECTOR_OUT = 176
const SEG = 22.5
const LABEL_R = RING_SECTOR_OUT + 14   // radius of the compass labels
const PAD = 30                          // viewBox padding so labels aren't clipped
const FADE_OPACITY = 0.28 // was 0.12 — kept higher so faded arcs stay visible/hoverable

function polar(b, r) {
  const rad = ((b - 90) * Math.PI) / 180
  return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)]
}
function arcPath(startB, endB, rInner, rOuter) {
  const [x1, y1] = polar(startB, rOuter)
  const [x2, y2] = polar(endB, rOuter)
  const [x3, y3] = polar(endB, rInner)
  const [x4, y4] = polar(startB, rInner)
  const large = endB - startB > 180 ? 1 : 0
  return `M ${x1} ${y1} A ${rOuter} ${rOuter} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${rInner} ${rInner} 0 ${large} 0 ${x4} ${y4} Z`
}
function sectorPath(i, rInner, rOuter) {
  return arcPath(i * SEG - SEG / 2, i * SEG + SEG / 2, rInner, rOuter)
}
function runs(indices) {
  const s = [...indices].sort((a, b) => a - b)
  const out = []
  let run = []
  for (const i of s) {
    if (run.length === 0 || i === run[run.length - 1] + 1) run.push(i)
    else { out.push(run); run = [i] }
  }
  if (run.length) out.push(run)
  if (out.length > 1 && out[0][0] === 0 && out[out.length - 1].at(-1) === 15) {
    const last = out.pop(); out[0] = [...last, ...out[0]]
  }
  return out
}
const hue = (i, n) => `hsl(${Math.round((360 * i) / Math.max(1, n))} 55% 42%)`

export default function WindRoseOption4({ sites, onSiteClick }) {
  const [activeSite, setActiveSite] = useState(null) // hovered individual site
  const [lockedDir, setLockedDir] = useState(null)    // click-locked direction
  const [hoverDir, setHoverDir] = useState(null)      // hover-preview direction

  // Refs to legend rows so the highlighted site can be scrolled into view even
  // when it sits below the legend's scroll fold (hovering a low arc used to
  // highlight a row you couldn't see).
  const legendRef = useRef(null)
  const rowRefs = useRef({})

  useEffect(() => {
    if (!activeSite) return
    const row = rowRefs.current[activeSite]
    if (row && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [activeSite])

  // Effective filter: a live hover preview wins over the locked selection.
  const selectedDir = hoverDir || lockedDir

  const ringCount = 6
  const ringStep = (RING_MAX - RING_MIN) / ringCount
  const ordered = useMemo(
    () => [...sites].sort((a, b) => a.name.localeCompare(b.name)),
    [sites]
  )

  const matchIds = useMemo(() => {
    if (!selectedDir) return null
    return new Set(
      sites.filter((s) => s.directions.includes(selectedDir)).map((s) => s.site_id || s.name)
    )
  }, [sites, selectedDir])
  const isMatch = (site) => !matchIds || matchIds.has(site.site_id || site.name)

  const countByDir = useMemo(() => {
    const c = Object.fromEntries(COMPASS_16.map((d) => [d, 0]))
    for (const s of sites) for (const d of s.directions) if (d in c) c[d]++
    return c
  }, [sites])
  const maxCount = Math.max(1, ...Object.values(countByDir))

  const legendSites = useMemo(
    () => (matchIds ? ordered.filter((s) => matchIds.has(s.site_id || s.name)) : ordered),
    [ordered, matchIds]
  )

  const activeSiteObj = useMemo(
    () => (activeSite ? ordered.find((s) => (s.site_id || s.name) === activeSite) : null),
    [ordered, activeSite]
  )

  const toggleLock = (d) => {
    setLockedDir((cur) => (cur === d ? null : d))
    setHoverDir(null)
  }

  return (
    <div className="wrp-a">
      <div className="wrp-4-rosewrap">
        <svg viewBox={`${-PAD} ${-PAD} ${SIZE + 2 * PAD} ${SIZE + 2 * PAD}`} className="wrp-a-svg" role="img"
             aria-label={selectedDir ? `Wind rose filtered to ${selectedDir}: ${legendSites.length} sites` : 'Wind rose of flying sites'}>
          {/* guide rings */}
          {Array.from({ length: ringCount + 1 }).map((_, i) => (
            <circle key={i} cx={CX} cy={CY} r={RING_MIN + i * ringStep} className="wrp-a-ring" />
          ))}

          {/* interactive outer compass-ring sectors: hover=preview, click=lock */}
          {COMPASS_16.map((d, i) => {
            const isLocked = d === lockedDir
            const isPreview = d === hoverDir
            const shade = 0.06 + 0.4 * (countByDir[d] / maxCount)
            return (
              <path
                key={`sec-${d}`}
                d={sectorPath(i, RING_SECTOR_IN, RING_SECTOR_OUT)}
                className={`wrp-4-sector ${isLocked ? 'is-locked' : ''} ${isPreview ? 'is-preview' : ''}`}
                style={{ fill: isLocked || isPreview ? undefined : `rgba(45,106,79,${shade})` }}
                onMouseEnter={() => setHoverDir(d)}
                onMouseLeave={() => setHoverDir(null)}
                onFocus={() => setHoverDir(d)}
                onBlur={() => setHoverDir(null)}
                onClick={() => toggleLock(d)}
                role="button"
                tabIndex={0}
                aria-pressed={isLocked}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), toggleLock(d))}
                aria-label={`Wind from ${d}: ${countByDir[d]} site${countByDir[d] === 1 ? '' : 's'}${isLocked ? ' (locked)' : ''}`}
              >
                <title>{`${d} — ${countByDir[d]} site${countByDir[d] === 1 ? '' : 's'} — click to ${isLocked ? 'unlock' : 'lock'}`}</title>
              </path>
            )
          })}

          {/* compass labels */}
          {COMPASS_16.map((d, i) => {
            const [lx, ly] = polar(i * SEG, LABEL_R)
            return (
              <text key={`l-${d}`} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle"
                    className={`wrp-a-compass ${i % 4 === 0 ? 'major' : ''} ${d === selectedDir ? 'wrp-4-sel' : ''}`}>{d}</text>
            )
          })}

          {/* site arcs — hovering an arc does NOT change the direction filter */}
          {ordered.map((site, idx) => {
            const key = site.site_id || site.name
            const ring = idx % ringCount
            const rI = RING_MIN + ring * ringStep + 2
            const rO = RING_MIN + (ring + 1) * ringStep - 2
            const match = isMatch(site)
            const activeOther = activeSite && activeSite !== key
            const faded = (!match) || activeOther
            return runs(site.directions.map((d) => COMPASS_16.indexOf(d))).map((run, ri) => (
              <path
                key={`${key}-${ri}`}
                d={arcPath(run[0] * SEG - SEG / 2, run.at(-1) * SEG + SEG / 2, rI, rO)}
                style={{ fill: hue(idx, ordered.length), opacity: faded ? FADE_OPACITY : 1 }}
                className={`wrp-a-arc ${activeSite === key ? 'is-active' : ''}`}
                onMouseEnter={() => setActiveSite(key)}
                onMouseLeave={() => setActiveSite(null)}
                onClick={() => {
                  // Reveal-then-open: the first tap identifies the site (highlights
                  // it + scrolls its legend row in); tapping the already-active site
                  // opens it. On mobile there's no hover, so this stops a stray first
                  // tap from navigating away before you've seen which site it is.
                  // (Desktop: hover already sets activeSite, so a click still opens.)
                  if (activeSite === key) onSiteClick?.(site)
                  else setActiveSite(key)
                }}
              >
                <title>{`${site.name} — ${site.directions.join(', ')}`}</title>
              </path>
            ))
          })}
        </svg>

        {/* Status + active-site banner live BELOW the rose in a fixed-height
            panel. Placing them above (and letting them appear/disappear) shifted
            the rose on hover, moving the cursor off the sector and causing a
            render/flicker loop. Reserved height keeps the rose position stable. */}
        <div className="wrp-4-panel">
          <p className="wrp-4-status" aria-live="polite">
            {selectedDir ? (
              <>
                Wind from <strong>{selectedDir}</strong> · {legendSites.length} flyable
                {lockedDir && <span className="wrp-4-lockpill">locked</span>}
                <button className="wrp-4-clear" onClick={() => { setLockedDir(null); setHoverDir(null) }}>
                  clear
                </button>
              </>
            ) : (
              <span className="wrp-4-hint">Hover a compass point to preview · click to lock</span>
            )}
          </p>

          {/* Active-site banner: shows which site was identified + an Open action. */}
          {activeSiteObj && (
            <div className="wrp-4-activebar">
              <span className="wrp-4-activename">{activeSiteObj.name}</span>
              <span className="wrp-4-activedirs">{activeSiteObj.directions.join(' · ')}</span>
              <button
                className="wrp-4-open"
                onClick={() => onSiteClick?.(activeSiteObj)}
              >
                Open guide →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* legend — filtered to matching sites when a direction is active */}
      <ul className="wrp-a-legend" ref={legendRef} onMouseLeave={() => setActiveSite(null)}>
        {legendSites.length === 0 && (
          <li className="wrp-a-legrow wrp-4-empty">No sites recorded for a {selectedDir} wind.</li>
        )}
        {legendSites.map((site) => {
          const key = site.site_id || site.name
          const idx = ordered.indexOf(site)
          return (
            <li key={key}
                ref={(el) => { if (el) rowRefs.current[key] = el; else delete rowRefs.current[key] }}
                className={`wrp-a-legrow ${activeSite === key ? 'is-active' : ''}`}
                onMouseEnter={() => setActiveSite(key)}
                onClick={() => onSiteClick?.(site)}>
              <span className="wrp-a-swatch" style={{ background: hue(idx, ordered.length) }} />
              <span className="wrp-a-legname">{site.name}</span>
              <span className="wrp-a-legdirs">{site.directions.join(' · ')}</span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
